import { useSelector } from "react-redux";
import { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useNavigate } from "react-router-dom";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelBody, PanelHeader } from "../../../components/panel/panel";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import ServiceTimeModal from "../serviceTimeModel";
import ImageUpload from "../../../components/CustomComponents/ImageUpload/imageUpload";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import showErrorToast, {
    showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import SaveActions from "../../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../../hooks/useUnsavedChangesGuard.js";

function AddDiningArea() {
    const CompID = useSelector((state) => state.auth.user).userData?.compID;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Get app settings from Redux
    const appSettings = useSelector((state) => state.appSettings);
    const { appTheme } = appSettings;

    // Check if dark mode is enabled
    const isDarkMode = useDarkMode();

    // Get theme color from centralized hook
    const themeColor = useThemeColor();

    const detailEditorModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "blockquote"],
            ["clean"],
        ],
    };


    const [servicePeriods, setServicePeriods] = useState([]);
    const [isServiceTimeModalOpen, setIsServiceTimeModalOpen] = useState(false);
    const [selectedServiceTimes, setSelectedServiceTimes] = useState([]);
    const [imageState, setImageState] = useState({
        imageUrl: "",
        imageFile: null,
    });
    const [formData, setFormData] = useState({
        sA_Name: "",
        sA_Detail: "",
        sA_Status: true,
        sA_Width: 0,
        sA_Height: 0,
        isRegularReservationCapable: true,
        isPrivateReservationCapable: false,
        isEventCapable: false,
        privateMinGuests: "",
        privateMaxGuests: "",
        eventMinGuests: "",
        eventMaxGuests: "",
        serviceAreaTables: [
            {
                t_ID: 0,
                localizeID: "en",
                t_Name: "",
                t_NoOfSeats: "",
                t_Shape: "",
                t_Status: "Available",
                t_Publish: true,
                t_Remarks: "",
                t_Xaxis: 0,
                t_Yaxis: 0,
                t_Version: 1,
                isTableMerged: false,
                mergedTableID: null,
            },
        ],
    });

    const initialFormSnapshotRef = useRef(null);

    const getFormSnapshot = useCallback(() => {
        return JSON.stringify({
            formData,
            selectedServiceTimes: (selectedServiceTimes || []).map((st) => ({
                id: st.id ?? st.sT_ID,
                sT_Name: st.sT_Name || "",
            })),
            imageUrl: imageState.imageUrl || "",
            imageFileName: imageState.imageFile?.name || "",
        });
    }, [formData, selectedServiceTimes, imageState.imageUrl, imageState.imageFile]);

    useEffect(() => {
        if (initialFormSnapshotRef.current === null) {
            initialFormSnapshotRef.current = getFormSnapshot();
        }
    }, [getFormSnapshot]);

    const isDirty =
        initialFormSnapshotRef.current !== null &&
        getFormSnapshot() !== initialFormSnapshotRef.current;

    const fetchServicePeriods = useCallback(async (options = {}) => {
        const silent = options.silent === true;
        try {
            if (!silent) setLoading(true);
            const response = await axiosInstance.get(
                `/api/ServiceTime/GetAllServiceTimes`,
                {
                    params: { CompID },
                }
            );
            setServicePeriods(response.data.data || []);
        } catch (error) {
            showErrorToast("Failed to load service periods");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [CompID]);

    useEffect(() => {
        if (CompID) {
            fetchServicePeriods();
        }
    }, [CompID, fetchServicePeriods]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTableChange = (index, field, value) => {
        const updatedTables = [...formData.serviceAreaTables];
        updatedTables[index] = { ...updatedTables[index], [field]: value };
        setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
    };

    const handleTableToggleStatus = (index) => {
        const updatedTables = [...formData.serviceAreaTables];
        updatedTables[index] = {
            ...updatedTables[index],
            t_Publish: !updatedTables[index].t_Publish,
        };
        setFormData((prev) => ({ ...prev, serviceAreaTables: updatedTables }));
    };

    const addTableRow = () => {
        setFormData((prev) => ({
            ...prev,
            serviceAreaTables: [
                ...prev.serviceAreaTables,
                {
                    t_ID: 0,
                    localizeID: "en",
                    t_Name: "",
                    t_NoOfSeats: "",
                    t_Shape: "",
                    t_Status: "Available",
                    t_Publish: true,
                    t_Remarks: "",
                    t_Xaxis: 0,
                    t_Yaxis: 0,
                    t_Version: 1,
                    isTableMerged: false,
                    mergedTableID: null,
                    angel: 0,
                },
            ],
        }));
    };

    const removeTableRow = (index) => {
        if (formData.serviceAreaTables.length === 1) {
            showErrorToast("At least one table is required");
            return;
        }
        setFormData((prev) => ({
            ...prev,
            serviceAreaTables: prev.serviceAreaTables.filter((_, i) => i !== index),
        }));
    };

    const handleServiceTimeSubmit = (selectedTimes) => {
        setSelectedServiceTimes(selectedTimes);
        setIsServiceTimeModalOpen(false);
    };

    const handleRemoveServiceTime = (serviceTime) => {
        setSelectedServiceTimes((prev) =>
            prev.filter((item) => item.id !== serviceTime.id)
        );
    };

    const handleImageChange = (url, file) => {
        setImageState({
            imageUrl: url,
            imageFile: file,
        });
    };

    const handleSubmit = async (options = {}) => {
        const { exitAfterSave = true } = options;
        try {
            if (!formData.sA_Name || formData.sA_Name.trim().length === 0) {
                showErrorToast("Please enter a valid dining area name");
                return false;
            }

            if (selectedServiceTimes.length === 0) {
                showErrorToast("Please select at least one service time");
                return false;
            }

            // Filter out empty draft table rows
            const validTables = formData.serviceAreaTables.filter(
                (table) =>
                    table.t_Name?.trim().length > 0 &&
                    table.t_NoOfSeats &&
                    !isNaN(table.t_NoOfSeats) &&
                    parseInt(table.t_NoOfSeats) > 0
            );

            if (validTables.length === 0) {
                showErrorToast("At least one valid table is required");
                return false;
            }

            // Validate private reservation capacity if enabled
            if (formData.isPrivateReservationCapable) {
                if (formData.privateMinGuests && formData.privateMaxGuests) {
                    const minGuests = parseInt(formData.privateMinGuests);
                    const maxGuests = parseInt(formData.privateMaxGuests);
                    if (minGuests >= maxGuests) {
                        showErrorToast("Private max guests must be greater than min guests");
                        return false;
                    }
                    if (minGuests < 1 || maxGuests < 1) {
                        showErrorToast("Private guest limits must be at least 1");
                        return false;
                    }
                }
            }

            // Validate event reservation capacity if enabled
            if (formData.isEventCapable) {
                if (formData.eventMinGuests && formData.eventMaxGuests) {
                    const minGuests = parseInt(formData.eventMinGuests);
                    const maxGuests = parseInt(formData.eventMaxGuests);
                    if (minGuests >= maxGuests) {
                        showErrorToast("Event max guests must be greater than min guests");
                        return false;
                    }
                    if (minGuests < 1 || maxGuests < 1) {
                        showErrorToast("Event guest limits must be at least 1");
                        return false;
                    }
                }
            }

            for (const table of validTables) {
                if (!table.t_Name || table.t_Name.trim().length === 0) {
                    showErrorToast("Please enter a valid table name for all tables");
                    return false;
                }
                if (
                    !table.t_NoOfSeats ||
                    isNaN(table.t_NoOfSeats) ||
                    parseInt(table.t_NoOfSeats) <= 0
                ) {
                    showErrorToast("Please enter a valid number of seats for all tables");
                    return false;
                }
            }

            setLoading(true);
            const now = new Date().toISOString();

            // Upload image first if provided
            let finalImageUrl = null;
            const baseURL = axiosInstance.defaults.baseURL || "";

            if (imageState.imageFile) {
                try {
                    const imageFormData = new FormData();
                    imageFormData.append("ImageFile", imageState.imageFile);

                    const imageResponse = await axiosInstance.post(
                        "/api/ServiceArea/UploadServiceAreaImage",
                        imageFormData,
                        {
                            headers: { "Content-Type": "multipart/form-data" },
                        }
                    );

                    if (imageResponse.data?.status && imageResponse.data?.data?.filePath) {
                        finalImageUrl = imageResponse.data.data.filePath;
                    }
                } catch (error) {
                    showErrorToast("Failed to upload image. Please try again.");
                    setLoading(false);
                    return false;
                }
            }

            // Calculate number of tables and total seats
            const numberOfTables = validTables.length;
            const numberOfSeats = validTables.reduce((total, table) => {
                return total + (parseInt(table.t_NoOfSeats) || 0);
            }, 0);

            const payload = {
                CompID,
                sA_Name: formData.sA_Name,
                sA_Detail: formData.sA_Detail,
                imageUrl: finalImageUrl,
                sA_Status: formData.sA_Status,
                sA_Width: formData.sA_Width,
                sA_Height: formData.sA_Height,
                sA_SortOrder: 0,
                addUpdateDate: now,
                NumberofTables: numberOfTables,
                NumberofSeats: numberOfSeats,
                // Always enable regular reservations (table-based)
                isRegularReservationCapable: true,
                isPrivateReservationCapable: formData.isPrivateReservationCapable,
                isEventCapable: formData.isEventCapable,
                privateMinGuests: formData.privateMinGuests ? parseInt(formData.privateMinGuests) : null,
                privateMaxGuests: formData.privateMaxGuests ? parseInt(formData.privateMaxGuests) : null,
                eventMinGuests: formData.eventMinGuests ? parseInt(formData.eventMinGuests) : null,
                eventMaxGuests: formData.eventMaxGuests ? parseInt(formData.eventMaxGuests) : null,
                ServiceAreaAndTimeRelations: selectedServiceTimes.map((item) => ({
                    id: 0,
                    sA_ID: 0,
                    sT_ID: Number(item.id),
                    compID: CompID,
                    addUpdateTime: now,
                })),
                ServiceAreaTables: validTables.map((table) => ({
                    t_ID: 0,
                    t_Name: table.t_Name,
                    t_NoOfSeats: parseInt(table.t_NoOfSeats),
                    t_Shape: table.t_Shape,
                    t_Status: table.t_Status,
                    localizeID: table.localizeID,
                    t_Xaxis: table.t_Xaxis,
                    t_Yaxis: table.t_Yaxis,
                    t_Publish: table.t_Publish,
                    t_Remarks: table.t_Remarks,
                    t_Version: table.t_Version,
                    isTableMerged: table.isTableMerged,
                    mergedTableID: table.mergedTableID,
                    updateDateTime: now,
                    compID: CompID,
                    angel: table.angel || 0, // Ensure angel is set to 0 if not provided
                    sA_ID: 0,
                })),
            };

            // Payload logged

            const response = await axiosInstance.post(
                "/api/ServiceArea/AddServiceArea",
                payload
            );

            if (response.data.status) {
                showSuccessToast("Dining area created successfully");
                initialFormSnapshotRef.current = getFormSnapshot();
                const newId =
                    response.data?.data?.id ??
                    response.data?.data?.ID ??
                    response.data?.data?.sA_ID;
                if (exitAfterSave) {
                    navigate("/diningAreas");
                } else if (newId && !Number.isNaN(Number(newId)) && Number(newId) > 0) {
                    navigate(`/diningAreas/updateDiningArea/${newId}`);
                } else {
                    navigate("/diningAreas");
                }
                return true;
            } else {
                showErrorToast(response.data.message || "Failed to create dining area");
                return false;
            }
        } catch (error) {
            // Create failed silently
            showErrorToast("Failed to create dining area");
            return false;
        } finally {
            setLoading(false);
        }
    };

    useUnsavedChangesGuard({
        isDirty,
        onSave: () => handleSubmit({ exitAfterSave: false }),
        onDiscard: () => {
            initialFormSnapshotRef.current = getFormSnapshot();
        },
    });

    const getSelectedServiceTimesText = () => {
        return selectedServiceTimes.map((st) => st.sT_Name).join(", ");
    };

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a href="#/">Home</a>
                        </li>
                        <li className="breadcrumb-item">
                            <a href="/dining-areas">Dining Areas</a>
                        </li>
                        <li className="breadcrumb-item active">Add Dining Area</li>
                    </ol>
                    <h1 className="page-header mb-0">Add Dining Area</h1>
                </div>
                <SaveActions
                    onCancel={() => navigate("/diningAreas")}
                    onSave={() => handleSubmit({ exitAfterSave: false })}
                    onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
                    saving={loading}
                    disabled={!formData.sA_Name?.trim()}
                />
            </div>
            <GridSectionLoader loading={loading} minHeight="320px">
            <div className="card border-0">
                <Panel className="m-3">
                    <PanelHeader>Add New Dining Area</PanelHeader>
                    <PanelBody>
                        <div className="p-2">
                            <div className="row">
                                
                                    <div className="col-md-7">

                                        <div className="col-md-12">

                                            <FormInput
                                                label="Dining Area Name"
                                                placeHolder="Enter dining area name"
                                                type="text"
                                                name="sA_Name"
                                                value={formData.sA_Name}
                                                onchange={handleInputChange}
                                                required
                                            />


                                        </div>
                                        <div className="col-md-12">
                                                 <label className="form-label">Service Time *</label>
                                                <div className="position-relative">
                                                <div
                                                        className="form-control d-flex align-items-center justify-content-between"
                                                        style={{
                                                            cursor: "pointer",
                                                            minHeight: "38px",
                                                            backgroundColor:
                                                                selectedServiceTimes.length > 0
                                                                    ? (isDarkMode ? "#111827" : "#ffffff")
                                                                    : (isDarkMode ? "#1f2937" : "#f8f9fa"),
                                                            border:
                                                                selectedServiceTimes.length > 0
                                                                    ? `1px solid ${themeColor}`
                                                                    : `1px solid ${isDarkMode ? "#374151" : "#ced4da"}`,
                                                            color: isDarkMode ? "#e5e7eb" : "inherit",
                                                            borderRadius: "8px",
                                                            boxShadow: isDarkMode ? "0 1px 2px rgba(0,0,0,0.4)" : "none"
                                                        }}
                                                        onClick={() => setIsServiceTimeModalOpen(true)}
                                                    >
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {selectedServiceTimes.length > 0 ? (
                                                                selectedServiceTimes.map((serviceTime) => (
                                                                    <span
                                                                        key={serviceTime.id}
                                                                        className="badge me-1 mb-1 d-flex align-items-center"
                                                                        style={{
                                                                            fontSize: "0.75rem",
                                                                            backgroundColor: themeColor,
                                                                            color: "#ffffff",
                                                                            borderRadius: "12px",
                                                                            padding: "6px 10px",
                                                                        }}
                                                                    >
                                                                        {serviceTime.sT_Name}
                                                                        <i
                                                                            className="fa fa-times ms-1"
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveServiceTime(serviceTime);
                                                                            }}
                                                                        ></i>
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className={isDarkMode ? "text-gray-400" : "text-muted"}>Select service times</span>
                                                            )}
                                                        </div>
                                                        {selectedServiceTimes.length > 0 && (
                                                            <i
                                                                className="fa fa-times-circle"
                                                                style={{
                                                                    cursor: "pointer",
                                                                    color: isDarkMode ? "#f87171" : "#dc3545",
                                                                    fontSize: "16px",
                                                                    marginLeft: "8px"
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedServiceTimes([]);
                                                                }}
                                                                title="Clear all selections"
                                                            ></i>
                                                        )}
                                                    </div>
                                                </div>
                                            
                                        </div>
                                        <div className="row d-none" >
                                            <div className="col-6 mb-3">
                                                <FormInput
                                                    label="Dining Area Width(px)"
                                                    placeHolder="Enter dining area Width"
                                                    type="number"
                                                    name="sA_Width"
                                                    step="0.01"
                                                    value={formData.sA_Width || 0}
                                                    onchange={handleInputChange}
                                                    required
                                                />
                                            </div>

                                            <div className="col-6 mb-3">
                                                <FormInput
                                                    label="Dining Area Height(px)"
                                                    placeHolder="Enter dining area Height"
                                                    type="number"
                                                    name="sA_Height"
                                                    step="0.01"
                                                    value={formData.sA_Height || 0}
                                                    onchange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                           
                                                <label className="form-label">Description</label>
                                                <div className={`service-area-rich-editor-wrap ${isDarkMode ? "dark" : ""}`}>
                                                    <ReactQuill
                                                        theme="snow"
                                                        value={formData.sA_Detail || ""}
                                                        onChange={(value) =>
                                                            setFormData((prev) => ({ ...prev, sA_Detail: value }))
                                                        }
                                                        modules={detailEditorModules}
                                                        placeholder="Enter detailed description..."
                                                        className="service-area-rich-editor"
                                                    />
                                                </div>
                                                 

                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-3">
                                        <label className="form-label">Service Area Image</label>
                                        <p className="text-muted mb-2" style={{ fontSize: '0.875rem', color: isDarkMode ? '#9ca3af' : '#6c757d' }}>
                                            Upload an image to showcase this service area. This will be displayed to customers when making reservations.
                                        </p>
                                        <ImageUpload
                                            imageUrl={imageState.imageUrl}
                                            setImage={handleImageChange}
                                            imageFile={imageState.imageFile}
                                            height="250px"
                                            width="100%"
                                            uploadText="Upload Service Area Image"
                                            buttonText="Change Image"
                                            centerButton={true}
                                            useGrid={false}
                                            className="mb-3"
                                        />
                                    </div>
                                 
                            </div>


                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label">Status</label>
                                    <div className="d-flex align-items-center">
                                        <ToggleSwitch
                                            isChecked={formData.sA_Status}
                                            onToggle={() =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    sA_Status: !prev.sA_Status,
                                                }))
                                            }
                                            label={formData.sA_Status ? "On" : "Off"}
                                            disabled={false}
                                            themeColor={themeColor}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Reservation Capabilities UI hidden as per requirements.
                                Regular reservations (table-based) are always enabled in the payload.
                                Private/Event capability can still be controlled via backend or future UI if needed. */}
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label mb-0">Tables</label>
                                        <div className="d-flex gap-3">
                                            <span className="badge bg-info">
                                                <i className="fa fa-table me-1"></i>
                                                Tables: {formData.serviceAreaTables.filter(table =>
                                                    table.t_Name?.trim().length > 0 &&
                                                    table.t_NoOfSeats &&
                                                    !isNaN(table.t_NoOfSeats) &&
                                                    parseInt(table.t_NoOfSeats) > 0
                                                ).length}
                                            </span>
                                            <span className="badge bg-success">
                                                <i className="fa fa-users me-1"></i>
                                                Seats: {formData.serviceAreaTables.reduce((total, table) => {
                                                    return total + (parseInt(table.t_NoOfSeats) || 0);
                                                }, 0)}
                                            </span>
                                        </div>
                                    </div>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Table Name</th>
                                                <th>Number of Seats</th>
                                                <th
                                                    style={{ textAlign: "center", verticalAlign: "middle" }}
                                                >
                                                    Shape
                                                </th>
                                                <th
                                                    style={{ textAlign: "center", verticalAlign: "middle" }}
                                                >
                                                    Publish
                                                </th>
                                                <th
                                                    style={{ textAlign: "center", verticalAlign: "middle" }}
                                                >
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.serviceAreaTables.map((table, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <FormInput
                                                            placeHolder="Enter table name"
                                                            type="text"
                                                            value={table.t_Name}
                                                            onchange={(e) =>
                                                                handleTableChange(index, "t_Name", e.target.value)
                                                            }
                                                            required
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormInput
                                                            placeHolder="Enter number of seats"
                                                            type="number"
                                                            value={table.t_NoOfSeats}
                                                            onchange={(e) =>
                                                                handleTableChange(
                                                                    index,
                                                                    "t_NoOfSeats",
                                                                    e.target.value
                                                                )
                                                            }
                                                            required
                                                            step="1"
                                                            min="1"
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex justify-content-center gap-2">
                                                            {["Square", "Rectangle", "Round"].map((shape) => (
                                                                <button
                                                                    key={shape}
                                                                    type="button"
                                                                    className={`btn btn-sm ${table.t_Shape === shape
                                                                        ? "btn-primary"
                                                                        : "btn-outline-secondary"
                                                                        }`}
                                                                    onClick={() => handleTableChange(index, "t_Shape", shape)}
                                                                    style={{
                                                                        width: "60px",
                                                                        height: "40px",
                                                                        borderRadius: "8px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "12px",
                                                                        fontWeight: "500"
                                                                    }}
                                                                    title={shape}
                                                                >
                                                                    {shape === "Square" && (
                                                                        <div
                                                                            style={{
                                                                                width: "20px",
                                                                                height: "20px",
                                                                                backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                                                                border: "1px solid",
                                                                                borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {shape === "Rectangle" && (
                                                                        <div
                                                                            style={{
                                                                                width: "24px",
                                                                                height: "16px",
                                                                                backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                                                                border: "1px solid",
                                                                                borderColor: table.t_Shape === shape ? "#fff" : "#6c757d"
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {shape === "Round" && (
                                                                        <div
                                                                            style={{
                                                                                width: "20px",
                                                                                height: "20px",
                                                                                backgroundColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                                                                border: "1px solid",
                                                                                borderColor: table.t_Shape === shape ? "#fff" : "#6c757d",
                                                                                borderRadius: "50%"
                                                                            }}
                                                                        />
                                                                    )}

                                                                </button>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="d-flex justify-content-center">
                                                        <ToggleSwitch
                                                            isChecked={table.t_Publish}
                                                            onToggle={() => handleTableToggleStatus(index)}
                                                            label={table.t_Publish ? "Active" : "Inactive"}
                                                            disabled={false}
                                                            themeColor={themeColor}
                                                        />
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: "center",
                                                            verticalAlign: "middle",
                                                        }}
                                                    >
                                                        {index === formData.serviceAreaTables.length - 1 ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={addTableRow}
                                                                >
                                                                    <i className="fa fa-plus"></i>
                                                                </button>{" "}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeTableRow(index)}
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => removeTableRow(index)}
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </PanelBody>
                </Panel>
            </div>
            </GridSectionLoader>

            <ServiceTimeModal
                isOpen={isServiceTimeModalOpen}
                onClose={() => setIsServiceTimeModalOpen(false)}
                onSubmit={handleServiceTimeSubmit}
                servicePeriods={servicePeriods}
                selectedServiceTimes={selectedServiceTimes}
                fetchServicePeriods={fetchServicePeriods}
            />
        </div>
    );
}

export default AddDiningArea;
